<template>
    <div>
        <hero-bar :has-right-visible="true" menu-icon="desktop-mac">
            {{ $lang.ResultsInfos.Title }}

            <p class="control" slot="right" style="margin-right: 16px">
                <b-button class="button is-gray" @click="previous">{{ $lang.Menus.BackBtn }}</b-button>
            </p>
            <!-- Not before the verdict is known: saving now would store the previous project's. -->
            <p class="control" slot="right">
                <b-button class="button is-primary" :disabled="verdict === null" @click="submit">{{ $lang.Menus.ContinueBtn }}</b-button>
            </p>
        </hero-bar>

        <div class="wizard">
            <div class="wizard-rail">
                <verticalStep-bar />
            </div>

            <div class="wizard-body">
                <div class="result-layout">
                    <!-- Up to 630 A: the four values the check compares. Above: the IEC TR 60890 chain. -->
                    <div>
                        <p class="wizard-card-head result-head">{{ $lang.ResultsInfos.Title_4 }}</p>
                        <div class="param-table">
                            <div v-for="row in paramRows" :key="row.key" class="param-row">
                                <span class="param-label">{{ row.label }}</span>
                                <span class="param-symbol" v-html="row.symbol"></span>
                                <span class="param-unit" v-html="row.unit"></span>
                                <span class="param-value">{{ row.value }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- The verdict: the band, and when it fails the three suggestions. -->
                    <div class="result-card" :class="'is-' + verdictState" role="status">
                        <div class="result-band">
                            <span class="mdi" :class="verdictIcon" aria-hidden="true"></span>
                            <div>
                                <p class="result-band-label">{{ $lang.ResultsInfos.Title }}</p>
                                <p class="result-band-text">{{ verdictText }}</p>
                            </div>
                        </div>
                        <div v-if="verdict === false" class="result-suggestions">
                            <p class="result-suggestions-head">{{ $lang.ResultsInfos.FailTitle }}</p>
                            <div v-for="key in ['Fail_1', 'Fail_2', 'Fail_3']" :key="key" class="suggestion-row">
                                <span class="mdi mdi-arrow-right" aria-hidden="true"></span>
                                <span>{{ $lang.ResultsInfos[key] }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="result-layout">
                    <!-- The distribution chart; its shape depends on Ae, as the 3/4-height row does. -->
                    <div v-if="!results_630 && (canValue_f_calculate || canValue_g_calculate)" class="result-figure">
                        <img :src="chartSrc" :alt="$lang.ResultsInfos.Title_4" />
                    </div>
                    <div v-else></div>

                    <div class="disclaimer-card">
                        <p class="disclaimer-title">{{ $lang.NewProject.Title }}</p>
                        <p>{{ $lang.NewProject.Info_1 }}</p>
                        <p>{{ $lang.NewProject.Info_2 }}</p>
                        <b-checkbox :value="true" disabled>{{ $lang.NewProject.Accept }}</b-checkbox>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import router from '@/router';
    import api from '@/api';
    import { toIpStandart } from '@/constants/protectionClass';
    import VerticalStepBar from '@/components/VerticalStepBar.vue';

    export default {
        name: 'Results',
        components: {
            VerticalStepBar,
        },
        data() {
            return {
                isActive: true,
                results_630: false,
                // null until the check has run; the store's isResultValid still holds the
                // previous project's verdict until then.
                verdict: null,
                calcFailed: false,

                panelTypeId: 0,
                virtualSurfaceIndex: 0,
                y: 0,
                g: 0,
                d: 0,
                envTemperature: 0,
                maxTemperature: 0,
                P_890: 0,
                P_Total: 0,
                result_630A: [],

                ip_Id: 0,
                Ae_value: 0,
                Sair_value: 0,
                value_k: 0,
                n_value: 0,
                value_d: 0,
                value_x: 0,
                deltaT_0_5: 0,
                Ab_val: 0,
                height_val: 0,
                canValue_f_calculate: false,
                value_f: 0,
                canValue_g_calculate: false,
                value_g: 0,
                value_c: 0,
                deltaT_0_75: 0,
                deltaT_1: 0,
                result_1600A: []
            }
        },
        mounted() {
            this.setResultTypeId();

            if (this.$store.state.totalLostPower_1 != null && this.$store.state.totalLostPower_1 != 0) {
                this.P_Total = this.$store.state.totalLostPower_1;
            } else if (this.$store.state.totalLostPower_2 != null && this.$store.state.totalLostPower_2 != 0) {
                this.P_Total = this.$store.state.totalLostPower_2;
            } else if (this.$store.state.totalLostPower_3 != null && this.$store.state.totalLostPower_3 != 0) {
                this.P_Total = this.$store.state.totalLostPower_3;
            }

            var panelDimension = this.$store.state.panelDimension;
            var array = panelDimension.split("x");
            this.y = array[0];
            this.g = array[1];
            this.d = array[2];
            this.panelTypeId = this.$store.state.panelTypeIndex;
            this.virtualSurfaceIndex = this.$store.state.virtualSurfaceIndex;
            this.envTemperature = this.$store.state.envTemperature;
            this.maxTemperature = this.$store.state.maxTemperature;
            this.getResult_630A();


            this.Ab_val = this.g * this.d;
            this.ip_Id = this.$store.state.selectedIP_Id;
            this.Ae_value = this.$store.state.ae_value;
            this.Sair_value = this.$store.state.Sair;
            this.n_value = this.$store.state.seperationNumber;
            if (this.$store.state.ratedCurrentIndex != 1) {
                this.calculate_DeltaT();
            }

            this.$store.state.activeStep = 4;
            this.$store.state.isFooterFixed = false;
        },
        computed: {
            paramRows() {
                const r = this.$lang.ResultsInfos;
                if (this.results_630) {
                    return [
                        { key: 'Ta', label: r.Ta, symbol: '<i>T</i><sub>a</sub>', unit: '°C', value: this.envTemperature },
                        { key: 'Tmax', label: r.Tmax, symbol: '<i>T</i><sub>int,max</sub>', unit: '°C', value: this.maxTemperature },
                        { key: 'P890', label: r.P890, symbol: '<i>P</i><sub>890</sub>', unit: 'W', value: this.truncate(this.P_890, 1) },
                        { key: 'P', label: r.Title_2, symbol: '<i>P</i>', unit: 'W', value: this.truncate(this.P_Total, 1) }
                    ];
                }
                const rows = [
                    { key: 'Sair', label: r.Sair, symbol: r.Sair_2, unit: r.cm2, value: this.$store.state.Sair },
                    { key: 'k', label: r.PanelConstant, symbol: '<i>k</i>', unit: '-', value: this.truncate(this.value_k, 3) },
                    { key: 'd', label: r.HorizontalFactor, symbol: '<i>d</i>', unit: '-', value: this.truncate(this.value_d, 2) },
                    { key: 'P', label: r.LostPower, symbol: '<i>P</i>', unit: 'W', value: this.truncate(this.P_Total, 1) },
                    { key: 'x', label: r.SupValue, symbol: '<i>x</i>', unit: '-', value: this.truncate(this.value_x, 3) },
                    { key: 'c', label: r.TempFactor, symbol: '<i>c</i>', unit: '-', value: this.truncate(this.value_c, 3) },
                    { key: 't05', label: r.TempIncrease, symbol: r.TempIncrease_result, unit: 'K', value: this.truncate(this.deltaT_0_5, 1) }
                ];
                if (this.canValue_g_calculate) {
                    rows.push({ key: 't075', label: r.TempInc_3_4, symbol: r.TempInc_3_4_result, unit: 'K', value: this.truncate(this.deltaT_0_75, 1) });
                }
                rows.push({ key: 't1', label: r.TempInc_UpPanel, symbol: r.TempInc_UpPanel_result, unit: 'K', value: this.truncate(this.deltaT_1, 1) });
                return rows;
            },
            verdictState() {
                if (this.verdict === true) return 'valid';
                if (this.verdict === false) return 'invalid';
                return this.calcFailed ? 'failed' : 'pending';
            },
            verdictText() {
                const r = this.$lang.ResultsInfos;
                return { valid: r.Success, invalid: r.Fail, failed: r.CalcFailed, pending: r.Pending }[this.verdictState];
            },
            verdictIcon() {
                return {
                    valid: 'mdi-check-circle',
                    invalid: 'mdi-alert-circle',
                    failed: 'mdi-alert-outline',
                    pending: 'mdi-timer-sand'
                }[this.verdictState];
            },
            chartSrc() {
                const lang = this.$lang.Format.Lang == 'tr-TR' ? 'TR' : 'EN';
                return '/img/' + (this.canValue_g_calculate ? 'Pano_2 ' : 'Pano_1 ') + lang + '.png';
            }
        },
        watch: {
            $route() {
                this.envTemperature = this.$store.state.envTemperature;
                this.maxTemperature = this.$store.state.maxTemperature;
            }
        },
        methods: {
            previous() {
                this.$store.isPageOpenedFromStepBar = false;
                this.$store.state.calcIndexFromOutputCircs = -1;

                if (this.$store.state.calcIndex == 3) {
                    router.push('/calc_Summary');
                } else {
                    router.push('/calculation');
                }
            },
            submit() {
                if (this.verdict === null) {
                    return;
                }

                var payload = { P_890: this.P_890, P_Total: this.P_Total, result_630A: this.result_630A };
                this.$store.commit('setResultInfos_630A', payload);

                var payload2 = {
                    value_k: this.value_k, n_value: this.n_value, value_d: this.value_d, value_x: this.value_x, deltaT_0_5: this.deltaT_0_5, Ab_val: this.Ab_val, height_val: this.height_val, value_f: this.value_f, value_g: this.value_g, value_c: this.value_c, deltaT_0_75: this.deltaT_0_75, deltaT_1: this.deltaT_1, result_1600A: this.result_1600A
                };
                this.$store.commit('setResultInfos_1600A', payload2);

                this.$store.isPageOpenedFromStepBar = false;
                this.$store.canReportPageOpen = true;

                router.push('/ReportPdfPage');

                // if (this.$store.state.memberId == 'TestMember') {
                //     router.push('/LoginMember');
                // } else {
                //     this.$store.state.isCompleted = true;
                //     router.push('/ReportPdfPage');
                // }
            },
            setResultTypeId() {
                if (this.$store.state.ratedCurrentIndex == 1) {
                    this.results_630 = true;
                } else {
                    this.results_630 = false;
                }
            },

            getResult_630A() {
                var ip = toIpStandart(this.$store.state.panelIP);

                api.panels.get_P_890(this.virtualSurfaceIndex, this.y, this.g, this.d, this.maxTemperature, this.envTemperature, ip).then((res) => {
                    if (res.isError) {
                        this.calcFailed = true;
                    } else {
                        this.P_890 = res.data;
                    }
                }).then(() => {
                    // Without P890 there is nothing to compare; no verdict rather than a wrong one.
                    if (this.calcFailed) {
                        return;
                    }
                    if (this.$store.state.ratedCurrentIndex == 1) {
                        var Pdifference = this.P_890 - this.P_Total;
                        if (Pdifference >= 0) {
                            this.result_630A = this.resultSentences(true);
                            this.verdict = true;
                            this.$store.state.isResultValid = true;
                            this.$store.canFanCalcOpen = false;
                        } else {
                            this.result_630A = this.resultSentences(false);
                            this.verdict = false;
                            this.$store.state.isResultValid = false;
                            this.$store.canFanCalcOpen = true;

                            ip = 55;

                            api.panels.get_P_890_Fan(this.virtualSurfaceIndex, this.y, this.g, this.d, this.maxTemperature, this.envTemperature, ip).then((res) => {
                                if (res.isError) {
                                    this.calcFailed = true;
                                } else {
                                    this.$store.state.P_890Fan = res.data;
                                }
                            });
                        }
                    }
                })
            },
            calculate_DeltaT() {
                var isIP31 = false;
                if (this.ip_Id == 2) {
                    isIP31 = true;
                }
                var sairval = this.Sair_value;
                if (sairval == 'N/A' || sairval == null) {
                    sairval = 0;
                }

                api.panels.get_k_Value(this.Ae_value, isIP31, sairval).then((res) => {
                    if (res.isError) {
                        this.calcFailed = true;
                    } else {
                        this.value_k = res.data;

                        api.panels.get_d_Value(this.Ae_value, isIP31, this.n_value).then((res) => {
                            if (res.isError) {
                                this.calcFailed = true;
                            } else {
                                this.value_d = res.data;

                                api.panels.get_x_Value(this.Ae_value, isIP31).then((res) => {
                                    if (res.isError) {
                                        this.calcFailed = true;
                                    } else {
                                        this.value_x = res.data;

                                        api.panels.get_DeltaT_0_5(this.value_k, this.value_d, this.P_Total, this.value_x).then((res) => {
                                            if (res.isError) {
                                                this.calcFailed = true;
                                            } else {
                                                this.deltaT_0_5 = res.data;

                                                this.get_DeltaT_1(isIP31);
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
            },

            get_DeltaT_1(isIP31) {
                if (this.Ae_value > 1.25) {
                    this.canValue_f_calculate = true;
                    this.canValue_g_calculate = false;
                    this.get_DeltaT_AeGreater(isIP31);
                } else {
                    this.canValue_f_calculate = false;
                    this.canValue_g_calculate = true;
                    this.get_DeltaT_AeSmaller(isIP31);
                }
            },
            get_DeltaT_AeGreater(isIP31) {
                api.panels.get_f_Value(this.y, this.g, this.d).then((res) => {
                    if (res.isError) {
                        this.calcFailed = true;
                    } else {
                        this.value_f = res.data;

                        var sairval = this.Sair_value;
                        if (sairval == 'N/A' || sairval == null) {
                            sairval = 0;
                        }

                        api.panels.get_c_Value(this.Ae_value, isIP31, this.panelTypeId, this.virtualSurfaceIndex, this.value_f, this.value_g, sairval).then((res) => {
                            if (res.isError) {
                                this.calcFailed = true;
                            } else {
                                this.value_c = res.data;
                                this.deltaT_1 = this.deltaT_0_5 * this.value_c;

                                this.getResult_1600A();
                            }
                        });
                    }
                });
            },
            get_DeltaT_AeSmaller(isIP31) {
                api.panels.get_g_Value(this.y, this.g).then((res) => {
                    if (res.isError) {
                        this.calcFailed = true;
                    } else {
                        this.value_g = res.data;

                        var sairval = this.Sair_value;
                        if (sairval == 'N/A' || sairval == null) {
                            sairval = 0;
                        }

                        api.panels.get_c_Value(this.Ae_value, isIP31, this.panelTypeId, this.virtualSurfaceIndex, this.value_f, this.value_g, sairval).then((res) => {
                            if (res.isError) {
                                this.calcFailed = true;
                            } else {
                                this.value_c = res.data;
                                this.deltaT_1 = this.deltaT_0_5 * this.value_c;
                                this.deltaT_0_75 = this.deltaT_1;

                                this.getResult_1600A();
                            }
                        });
                    }
                });
            },

            getResult_1600A() {
                var _temp1 = parseFloat(this.envTemperature);
                var _temp2 = parseFloat(this.deltaT_1);
                var _temp3 = parseFloat(this.maxTemperature);
                var Tdifference = _temp3 - (_temp1 + _temp2);
                if (Tdifference >= 0) {
                    this.result_1600A = this.resultSentences(true);
                    this.verdict = true;
                    this.$store.state.isResultValid = true;
                    this.$store.canFanCalcOpen = false;
                } else {
                    this.result_1600A = this.resultSentences(false);
                    this.verdict = false;
                    var ip = 55;

                    api.panels.get_P_890_Fan(this.virtualSurfaceIndex, this.y, this.g, this.d, this.maxTemperature, this.envTemperature, ip).then((res) => {
                        if (res.isError) {
                            this.calcFailed = true;
                        } else {
                            this.$store.state.P_890Fan = res.data;
                        }
                    });

                    this.$store.state.isResultValid = false;
                    this.$store.canFanCalcOpen = true;
                }
            },

            // The sentences the store keeps for the verdict, in the order the old code pushed them.
            resultSentences(valid) {
                const r = this.$lang.ResultsInfos;
                return valid ? [r.Success] : [r.Fail, r.FailTitle, r.Fail_1, r.Fail_2, r.Fail_3];
            },

            truncate(numToBeTruncated, numOfDecimals) {
                var theNumber = numToBeTruncated.toString();
                if (theNumber.includes('.')) {
                    var pointIndex = theNumber.indexOf('.');
                    var _result = +(theNumber.slice(0, pointIndex > -1 ? ++numOfDecimals + pointIndex : undefined));
                    var _resultString = _result.toString();
                    if (!_resultString.includes('.')) {
                        return _resultString + '.0';
                    } else {
                        return _result;
                    }
                } else {
                    return theNumber + '.0';
                }
            },
        },

    };
</script>
